import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import ParticlesBackground from '../components/ParticlesBackground';
import MobileMockup from '../components/MobileMockup';
import marketDataService from '../services/marketDataService';
import { 
  ChartBarIcon, 
  ShieldCheckIcon, 
  CurrencyDollarIcon,
  GlobeAltIcon,
  ClockIcon,
  StarIcon,
  ArrowTrendingUpIcon,
  UserGroupIcon,
  ChevronRightIcon,
  PlayIcon,
  BoltIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  AcademicCapIcon,
  TrophyIcon,
  CheckBadgeIcon,
  RocketLaunchIcon,
  BanknotesIcon,
  ChartPieIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  FireIcon,
  EyeIcon,
  DocumentDuplicateIcon,
  ArrowRightIcon,
  BuildingOfficeIcon,
  UsersIcon,
  CpuChipIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';

// Import video
import heroVideo from '../assets/6710903b7e9e9d521cf7f597-sf2m6mr5ng__c2cef81ea7577bfa6ab98cd8f635cba8__P360.mp4';

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [activeMarketTab, setActiveMarketTab] = useState('crypto');
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 300], [0, 100]);
  
  // Assets for logo slider
  const stockTickers = ['AAPL', 'GOOGL', 'MSFT', 'TSLA', 'AMZN', 'META', 'NVDA', 'NFLX'];
  const cryptoSymbols = ['btc', 'eth', 'sol', 'ada', 'dot', 'matic', 'avax', 'link'];
  
  // Combine stocks and crypto for the slider
  const allAssets = [
    ...stockTickers.map(ticker => ({ type: 'stock', symbol: ticker })),
    ...cryptoSymbols.map(symbol => ({ type: 'crypto', symbol: symbol.toUpperCase() }))
  ];
  
  // Duplicate assets for seamless loop
  const duplicatedAssets = [...allAssets, ...allAssets];
  
  // State for asset prices
  const [assetPrices, setAssetPrices] = useState({});
  
  // Live market data state
  const [liveMarketData, setLiveMarketData] = useState({
    crypto: [],
    stocks: [],
    indices: []
  });
  const [isLoading, setIsLoading] = useState(true);

  // Fetch live market data
  const fetchMarketData = async () => {
    setIsLoading(true);
    try {
      const [cryptoData, stockPrices, indices] = await Promise.all([
        marketDataService.getCryptoPrices(),
        marketDataService.getStockPrices(),
        marketDataService.getMarketIndices()
      ]);

      setLiveMarketData({
        crypto: cryptoData || [],
        stocks: stockPrices || [],
        indices: indices || []
      });
    } catch (error) {
      console.error('Error fetching market data:', error);
      setLiveMarketData({
        crypto: [],
        stocks: [],
        indices: []
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch prices for slider assets
  const fetchSliderAssetPrices = async () => {
    try {
      const [stockData, cryptoData] = await Promise.all([
        marketDataService.getStockPrices(),
        marketDataService.getCryptoPrices()
      ]);
      
      const pricesMap = {};
      
      // Map stock prices
      if (stockData && Array.isArray(stockData)) {
        stockData.forEach(stock => {
          if (stock && stock.symbol) {
            pricesMap[stock.symbol] = {
              price: parseFloat(stock.price) || 0,
              change: parseFloat(stock.changePercent || stock.change) || 0,
              positive: stock.positive !== undefined ? stock.positive : (parseFloat(stock.changePercent || stock.change) || 0) >= 0
            };
          }
        });
      }
      
      // Map crypto prices - handle both symbol formats
      if (cryptoData && Array.isArray(cryptoData)) {
        cryptoData.forEach(crypto => {
          if (crypto && crypto.symbol) {
            pricesMap[crypto.symbol] = {
              price: parseFloat(crypto.price) || 0,
              change: parseFloat(crypto.change) || 0,
              positive: crypto.positive !== undefined ? crypto.positive : (parseFloat(crypto.change) || 0) >= 0
            };
          }
        });
      }
      
      setAssetPrices(pricesMap);
    } catch (error) {
      console.error('Error fetching slider asset prices:', error);
    }
  };

  // Initial data fetch and periodic updates
  useEffect(() => {
    fetchMarketData();
    fetchSliderAssetPrices();
    
    // Update every 30 seconds
    const interval = setInterval(() => {
      fetchMarketData();
      fetchSliderAssetPrices();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const [marketData, setMarketData] = useState([
    { symbol: 'S&P 500', price: '4,567.89', change: '+1.23%', positive: true },
    { symbol: 'NASDAQ', price: '14,321.45', change: '+0.87%', positive: true },
    { symbol: 'BTC/USD', price: '45,678.90', change: '-2.45%', positive: false },
    { symbol: 'ETH/USD', price: '3,234.56', change: '+3.21%', positive: true },
    { symbol: 'GOLD', price: '1,987.45', change: '+0.62%', positive: true },
    { symbol: 'EUR/USD', price: '1.0856', change: '+0.21%', positive: true },
  ]);

  // Advanced features showcase
  const advancedFeatures = [
    {
      icon: BoltIcon,
      title: 'Lightning-Fast Execution',
      description: 'Execute trades in milliseconds with our cutting-edge infrastructure and direct market access.',
      stats: '< 10ms',
      color: 'bg-yellow-500'
    },
    {
      icon: ShieldCheckIcon,
      title: 'Military-Grade Security',
      description: 'Your assets are protected by the same encryption technology used by global financial institutions.',
      stats: '256-bit SSL',
      color: 'bg-green-500'
    },
    {
      icon: GlobeAltIcon,
      title: 'Global Market Access',
      description: 'Trade across 50+ exchanges worldwide with unified portfolio management and real-time data.',
      stats: '50+ Exchanges',
      color: 'bg-blue-500'
    },
    {
      icon: ChartBarIcon,
      title: 'AI-Powered Analytics',
      description: 'Advanced machine learning algorithms provide personalized insights and market predictions.',
      stats: '99.7% Accuracy',
      color: 'bg-purple-500'
    }
  ];


  const features = [
    {
      icon: ChartBarIcon,
      title: 'Advanced Trading Tools',
      description: 'Professional-grade charts, technical indicators, and real-time market analysis powered by institutional-quality data feeds.',
      gradient: 'from-blue-500 to-blue-600'
    },
    {
      icon: ShieldCheckIcon,
      title: 'Bank-Level Security',
      description: 'Your investments and personal data are protected with military-grade encryption, cold storage, and multi-factor authentication.',
      gradient: 'from-green-500 to-green-600'
    },
    {
      icon: CurrencyDollarIcon,
      title: 'Ultra-Low Fees',
      description: 'Industry-leading low commission rates and transparent pricing with no hidden fees. Trade more, pay less.',
      gradient: 'from-yellow-500 to-yellow-600'
    },
    {
      icon: GlobeAltIcon,
      title: 'Global Markets',
      description: 'Access to stocks, crypto, and derivatives from markets around the world, all in one unified platform.',
      gradient: 'from-purple-500 to-purple-600'
    },
    {
      icon: ClockIcon,
      title: '24/7 Trading',
      description: 'Trade cryptocurrencies 24/7 and access extended-hours stock trading to never miss an opportunity.',
      gradient: 'from-indigo-500 to-indigo-600'
    },
    {
      icon: UserGroupIcon,
      title: 'Expert Support',
      description: 'Dedicated relationship managers, 24/7 technical support, and access to market experts and research analysts.',
      gradient: 'from-pink-500 to-pink-600'
    },
    {
      icon: AcademicCapIcon,
      title: 'Educational Resources',
      description: 'Comprehensive learning center with webinars, tutorials, market analysis, and trading courses for all skill levels.',
      gradient: 'from-red-500 to-red-600'
    },
    {
      icon: RocketLaunchIcon,
      title: 'Algorithmic Trading',
      description: 'Build and deploy automated trading strategies with our sophisticated algorithmic trading platform.',
      gradient: 'from-cyan-500 to-cyan-600'
    },
    {
      icon: ChartPieIcon,
      title: 'Portfolio Analytics',
      description: 'Advanced portfolio analysis, risk metrics, performance attribution, and tax optimization tools.',
      gradient: 'from-orange-500 to-orange-600'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Chen',
      role: 'Professional Day Trader',
      company: 'Quantitative Capital',
      content: 'The execution speed is phenomenal. I\'ve increased my trading efficiency by 300% and my profits by 40% since switching to IMEXS BROKERAGE.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
      stats: '+300% Efficiency'
    },
    {
      name: 'Michael Rodriguez',
      role: 'Portfolio Manager',
      company: 'Global Asset Management',
      content: 'The research tools and AI-powered analytics are game-changing. It\'s like having a Bloomberg terminal with better UX.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
      stats: '$50M+ Managed'
    },
    {
      name: 'Emily Thompson',
      role: 'Crypto Hedge Fund Manager',
      company: 'Digital Assets Fund',
      content: 'Finally, a platform that takes security seriously while providing institutional-grade trading capabilities.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face',
      stats: '2,000+ BTC Traded'
    },
    {
      name: 'David Kim',
      role: 'Algorithmic Trader',
      company: 'Quantum Trading Systems',
      content: 'The API is robust and the execution is lightning-fast. Perfect for high-frequency trading strategies.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
      stats: '1M+ Trades/Month'
    }
  ];

  const stats = [
    { label: 'Active Traders', value: '250K+', description: 'Professional traders worldwide' },
    { label: 'Daily Volume', value: '$12.5B+', description: 'Average daily trading volume' },
    { label: 'Assets Available', value: '10,000+', description: 'Stocks, crypto & more' },
    { label: 'Uptime', value: '99.99%', description: 'Platform reliability guarantee' },
    { label: 'Countries', value: '190+', description: 'Global market coverage' },
    { label: 'Support Response', value: '< 30s', description: 'Average response time' }
  ];

  const awards = [
    { title: 'Best Trading Platform 2024', organization: 'FinTech Awards' },
    { title: 'Most Innovative Broker', organization: 'Trading Excellence' },
    { title: 'Top Security Standards', organization: 'CyberSec Institute' },
    { title: 'Best Customer Service', organization: 'Broker Review' }
  ];

  const tradingAssets = [
    { 
      category: 'Stocks', 
      count: '5,000+', 
      description: 'US, European, Asian equities',
      icon: '📈',
      features: ['Real-time quotes', 'After-hours trading', 'Fractional shares']
    },
    { 
      category: 'Cryptocurrencies', 
      count: '200+', 
      description: 'Major and altcoins',
      icon: '₿',
      features: ['24/7 trading', 'DeFi tokens', 'Staking rewards']
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);


  // Update market data periodically
  useEffect(() => {
    const interval = setInterval(() => {
      setMarketData(prev => prev.map(item => ({
        ...item,
        change: `${Math.random() > 0.5 ? '+' : '-'}${(Math.random() * 3).toFixed(2)}%`,
        positive: Math.random() > 0.5
      })));
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  // Function to get logo URL
  const getLogoUrl = (asset) => {
    if (asset.type === 'stock') {
      return `https://img.logo.dev/ticker/${asset.symbol}?token=pk_Pb2cclXnTuuh7Lsj1Ys7ew&retina=true`;
    } else {
      return `https://assets.coincap.io/assets/icons/${asset.symbol.toLowerCase()}@2x.png`;
    }
  };

  return (
    <div className="min-h-screen w-full overflow-x-hidden">
      {/* Modern Split-Screen Hero Section */}
      <section className="relative min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-black overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-amber-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-yellow-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-amber-400/5 rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10 min-h-screen flex items-center pb-20 md:pb-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 w-full py-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              
              {/* Left Side - Content */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="space-y-8"
              >
                {/* Main Heading */}
                <motion.h1
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  className="text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-tight"
                >
                  <span className="text-white">Trade Smarter,</span>
                  <br />
                  <span className="bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 bg-clip-text text-transparent">
                    Trade Faster
                  </span>
                </motion.h1>

                {/* Description */}
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="text-lg sm:text-xl text-gray-400 leading-relaxed max-w-lg"
                >
                  Experience next-generation trading with lightning-fast execution, 
                  advanced analytics, and institutional-grade security. Join the future of finance.
                </motion.p>

                {/* CTA Buttons */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.5 }}
                  className="flex flex-wrap gap-4"
                >
                  <Link
                    to="/register"
                    className="group relative px-8 py-4 bg-gradient-to-r from-amber-600 to-yellow-600 text-white font-semibold rounded-xl overflow-hidden transition-all duration-300 hover:shadow-2xl hover:shadow-amber-500/50 hover:scale-105"
                  >
                    <span className="relative z-10 flex items-center">
                      Start Trading
                      <ArrowRightIcon className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </span>
                    <div className="absolute inset-0 bg-gradient-to-r from-amber-700 to-yellow-700 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  </Link>
                  <Link
                    to="/login"
                    className="px-8 py-4 bg-white/5 backdrop-blur-sm border border-white/10 text-white font-semibold rounded-xl hover:bg-white/10 hover:border-white/20 transition-all duration-300"
                  >
                    Sign In
                  </Link>
                </motion.div>
              </motion.div>

              {/* Right Side - Video Showcase */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative"
              >
                {/* Video Container with Modern Design */}
                <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-white/10 bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm">
                  {/* Glow Effect */}
                  <div className="absolute -inset-1 bg-gradient-to-r from-amber-600 via-yellow-500 to-amber-600 rounded-3xl opacity-20 blur-xl"></div>
                  
                  <div className="relative rounded-3xl overflow-hidden">
                    <video
                      autoPlay
                      muted
                      loop
                      playsInline
                      className="w-full h-auto aspect-video object-cover"
                    >
                      <source src="https://res.cloudinary.com/de6gefjkd/video/upload/v1764053200/191304-889685064_tiny_qz6qyr.mp4" type="video/mp4" />
                    </video>
                  </div>

                  {/* Floating Stats Cards */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.8 }}
                    className="absolute -bottom-6 -left-6 bg-gradient-to-br from-slate-900/95 to-slate-800/95 backdrop-blur-md border border-white/10 rounded-2xl p-5 shadow-2xl"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                        <ArrowTrendingUpIcon className="w-6 h-6 text-amber-400" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-white">$2.5B+</div>
                        <div className="text-xs text-gray-400">Daily Volume</div>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 1 }}
                    className="absolute -top-6 -right-6 bg-gradient-to-br from-slate-900/95 to-slate-800/95 backdrop-blur-md border border-white/10 rounded-2xl p-5 shadow-2xl"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                        <UserGroupIcon className="w-6 h-6 text-yellow-400" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-white">500K+</div>
                        <div className="text-xs text-gray-400">Active Traders</div>
                      </div>
                    </div>
                  </motion.div>
                </div>

                {/* Decorative Elements */}
                <div className="absolute top-1/4 -right-8 w-32 h-32 bg-amber-500/10 rounded-full blur-2xl"></div>
                <div className="absolute bottom-1/4 -left-8 w-40 h-40 bg-yellow-500/10 rounded-full blur-2xl"></div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Asset Logos Slider - Below Hero Content */}
        <div className="absolute bottom-0 left-0 right-0 z-20 overflow-hidden">
          <div className="py-3 md:py-4">
            <div className="flex animate-scroll gap-4 will-change-transform">
              {duplicatedAssets.map((asset, index) => {
                const priceData = assetPrices[asset.symbol] || null;
                const formattedPrice = priceData ? 
                  (priceData.price < 1 ? priceData.price.toFixed(4) : priceData.price.toFixed(2)) : 
                  '--';
                const changePercent = priceData ? priceData.change.toFixed(2) : '--';
                const isPositive = priceData ? priceData.positive : null;
                
                return (
                  <div
                    key={`${asset.type}-${asset.symbol}-${index}`}
                    className="flex-shrink-0 flex items-center gap-3 px-4 py-2.5 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 hover:bg-white/20 hover:border-amber-400/50 hover:shadow-lg transition-all duration-300 group"
                  >
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                      <img
                        src={getLogoUrl(asset)}
                        alt={asset.symbol}
                        className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-300"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                    {priceData ? (
                      <div className="flex flex-col gap-0.5">
                        <div className="text-sm font-semibold text-white whitespace-nowrap">
                          ${formattedPrice}
                        </div>
                        <div className={`text-xs font-medium whitespace-nowrap ${
                          isPositive ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {isPositive ? '+' : ''}{changePercent}%
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-400">--</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          {/* Gradient fade on edges */}
          <div className="absolute top-0 left-0 w-32 h-full bg-gradient-to-r from-slate-950 to-transparent pointer-events-none z-10"></div>
          <div className="absolute top-0 right-0 w-32 h-full bg-gradient-to-l from-slate-950 to-transparent pointer-events-none z-10"></div>
        </div>
      </section>

      {/* About Us Section */}
      <section className="relative pt-6 pb-24 lg:pt-8 lg:pb-32 bg-white overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl"></div>
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-stretch">
            {/* Content Side */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="flex flex-col justify-center space-y-8 relative z-10"
            >
              {/* Decorative line */}
              <div className="absolute -left-8 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-amber-500/30 to-transparent hidden lg:block"></div>
              
              <div className="space-y-6">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                    Your Trusted Partner in
                    <span className="block bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-600 bg-clip-text text-transparent mt-2">
                      Global Trading
                    </span>
                  </h2>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="space-y-5"
                >
                  <p className="text-xl text-gray-600 leading-relaxed">
                    IMEXS BROKERAGE is a leading financial services provider committed to delivering 
                    exceptional trading experiences. With years of expertise in global markets, we 
                    empower traders and investors with cutting-edge technology and comprehensive 
                    market access.
                  </p>

                  <p className="text-xl text-gray-600 leading-relaxed">
                    Our platform combines institutional-grade tools with user-friendly interfaces, 
                    making professional trading accessible to everyone. We prioritize security, 
                    transparency, and innovation in everything we do.
                  </p>
                </motion.div>
              </div>

              {/* Accent elements */}
              <div className="flex items-center gap-4 mt-8">
                <div className="h-px w-16 bg-gradient-to-r from-amber-500 to-transparent"></div>
                <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
              </div>
            </motion.div>

            {/* Video Side */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative w-full mt-8 lg:mt-0 flex flex-col justify-center"
            >
              <div className="relative w-full rounded-3xl overflow-hidden border-2 border-gray-200 shadow-xl group hover:border-amber-500 transition-all duration-500 aspect-[16/10]">
                {/* Glow effect */}
                <div className="absolute -inset-1 bg-gradient-to-r from-amber-500/10 via-yellow-500/10 to-amber-500/10 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <video
                  src="https://res.cloudinary.com/de6gefjkd/video/upload/v1764053201/308077_tiny_eymeef.mp4"
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                
                {/* Subtle overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-white/20 via-transparent to-transparent"></div>
              </div>
              
              {/* Stats Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="grid grid-cols-3 gap-4 mt-6"
              >
                <div className="text-center p-4 bg-gray-50 rounded-xl border border-gray-200 hover:border-amber-500 transition-all duration-300">
                  <div className="text-3xl lg:text-4xl font-bold text-gray-900 mb-1">500K+</div>
                  <div className="text-sm text-gray-600">Active Traders</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-xl border border-gray-200 hover:border-amber-500 transition-all duration-300">
                  <div className="text-3xl lg:text-4xl font-bold text-gray-900 mb-1">$50B+</div>
                  <div className="text-sm text-gray-600">Trading Volume</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-xl border border-gray-200 hover:border-amber-500 transition-all duration-300">
                  <div className="text-3xl lg:text-4xl font-bold text-gray-900 mb-1">150+</div>
                  <div className="text-sm text-gray-600">Countries</div>
                </div>
              </motion.div>
              
              {/* Decorative elements */}
              <div className="absolute -bottom-8 -right-8 w-40 h-40 bg-amber-500/5 rounded-full blur-3xl"></div>
              <div className="absolute top-1/2 -left-6 w-24 h-24 bg-yellow-500/5 rounded-full blur-2xl"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="relative py-24 lg:py-32 bg-black overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 right-0 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl"></div>
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center gap-3 mb-6">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-amber-500"></div>
              <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-amber-500"></div>
            </div>
            <h2 className="text-5xl lg:text-6xl font-bold text-white mb-6">
              Our Trading Services
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Comprehensive trading solutions designed for traders of all levels
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
            {[
              {
                image: '/images/social-media-3846597_640.png',
                title: 'Copy Trading',
                description: 'Automatically copy trades from top-performing traders. Learn from experts and diversify your portfolio with proven strategies.',
                href: '/copy-trading'
              },
              {
                image: '/images/bot-trade.jpg',
                title: 'Bots Trading',
                description: 'Automated trading bots that execute strategies 24/7. Set your parameters and let AI-powered algorithms work for you.',
                href: '/bots-trading'
              },
              {
                image: '/images/trading.png',
                title: 'Live Trading',
                description: 'Real-time trading with lightning-fast execution. Trade directly on live markets with professional-grade tools and analytics.',
                href: '/live-trading'
              },
              {
                image: '/images/stock-market-economic-graph-with-diagrams-business-financial-concepts-reports-abstract-technology-communication-concept-background_29865-1793.jpg',
                title: 'Stocks Trading',
                description: 'Access global stock markets with real-time quotes, advanced charting, and comprehensive market analysis tools.',
                href: '/markets?type=stocks'
              },
              {
                image: '/images/bitcoin-cryptocurrency-trading-with-candlestick-technical-analytics_441990-14552.jpg',
                title: 'Crypto Trading',
                description: 'Trade 200+ cryptocurrencies with low fees and high liquidity. Access DeFi tokens, staking, and advanced crypto tools.',
                href: '/markets?type=crypto'
              },
              {
                image: '/images/brokers-shaking-hands-with-successful-deal_236854-49521.jpg',
                title: 'P2P Trading',
                description: 'Direct peer-to-peer trading platform. Trade directly with other users, set your own rates, and enjoy secure escrow services.',
                href: '/p2p-trading'
              }
            ].map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group relative"
              >
                <div className="relative h-full bg-gradient-to-br from-slate-950 via-slate-900 to-amber-950/20 rounded-3xl border-2 border-amber-500/50 overflow-hidden transition-all duration-500 hover:border-amber-400/70 hover:shadow-2xl hover:shadow-amber-500/20 hover:-translate-y-2 group">
                  {/* Glow effect on hover */}
                  <div className="absolute -inset-1 bg-gradient-to-r from-amber-500/0 via-yellow-500/0 to-amber-500/0 group-hover:from-amber-500/20 group-hover:via-yellow-500/20 group-hover:to-amber-500/20 rounded-3xl blur-xl transition-all duration-500"></div>
                  
                  {/* Image Section */}
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    {/* Subtle overlay for better text readability */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                  </div>
                  
                  {/* Content Section */}
                  <div className="relative p-8 bg-gradient-to-b from-transparent to-slate-950/80">
                    {/* Title and Description */}
                    <div className="relative mb-6">
                      <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4 group-hover:text-amber-300 transition-colors duration-300">
                        {service.title}
                      </h3>
                      <p className="text-gray-300 leading-relaxed text-base">
                        {service.description}
                      </p>
                    </div>

                    {/* Action Button */}
                    <div className="relative">
                      <Link
                        to={service.href}
                        className="inline-flex items-center justify-center w-full px-6 py-3 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white font-semibold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-amber-500/50 group-hover:scale-105"
                      >
                        Learn More
                        <ArrowRightIcon className="ml-2 h-5 w-5" />
                      </Link>
                    </div>

                    {/* Bottom accent line */}
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-amber-500 to-transparent opacity-50 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>

                  {/* Corner accent */}
                  <div className="absolute top-4 right-4 w-3 h-3 bg-amber-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10 shadow-lg shadow-amber-500/50"></div>
                  
                  {/* Top border accent */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-amber-500/0 to-transparent group-hover:via-amber-500 transition-all duration-300"></div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* Copy Trading Section - Modern Redesign */}
      <section className="relative py-24 lg:py-32 bg-gradient-to-b from-white to-gray-50 overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 right-0 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl"></div>
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 z-10">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center gap-3 mb-6">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-amber-500"></div>
              <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-amber-500"></div>
            </div>
            
            <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Follow Elite Traders
              <span className="block bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-600 bg-clip-text text-transparent mt-2">
                Copy Their Success
              </span>
            </h2>
            
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Automatically mirror trades from top-performing professionals. Start earning from day one with proven strategies.
            </p>
          </motion.div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
            {/* Top Traders Cards */}
            {[
              { 
                name: 'Alexander Chen', 
                username: '@alextrader', 
                profit: '+324.7%', 
                winRate: '87%',
                monthlyReturn: '+24.3%',
                risk: 'Medium',
                copiers: '2.1K',
                avatar: 'AC',
                status: 'trading'
              },
              { 
                name: 'Sarah Williams', 
                username: '@sarahfx', 
                profit: '+198.5%', 
                winRate: '92%',
                monthlyReturn: '+18.7%',
                risk: 'Low',
                copiers: '1.8K',
                avatar: 'SW',
                status: 'online'
              },
              { 
                name: 'Marcus Rodriguez', 
                username: '@cryptoking', 
                profit: '+445.2%', 
                winRate: '78%',
                monthlyReturn: '+31.2%',
                risk: 'High',
                copiers: '3.5K',
                avatar: 'MR',
                status: 'trading'
              }
            ].map((trader, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group relative bg-white rounded-3xl border-2 border-gray-200 overflow-hidden transition-all duration-500 hover:border-amber-500 hover:shadow-2xl hover:shadow-amber-500/10 hover:-translate-y-2"
              >
                {/* Glow effect */}
                <div className="absolute -inset-1 bg-gradient-to-r from-amber-500/0 via-yellow-500/0 to-amber-500/0 group-hover:from-amber-500/10 group-hover:via-yellow-500/10 group-hover:to-amber-500/10 rounded-3xl blur-xl transition-all duration-500"></div>
                
                <div className="relative p-6">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-4">
                      <div className="relative">
                        <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-xl flex items-center justify-center text-white font-bold text-lg">
                          {trader.avatar}
                        </div>
                        <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-white ${
                          trader.status === 'trading' ? 'bg-green-500 animate-pulse' : 'bg-blue-500'
                        }`}></div>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">{trader.name}</h3>
                        <p className="text-sm text-gray-500">{trader.username}</p>
                      </div>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="space-y-4 mb-6">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                      <span className="text-sm text-gray-600">Total Return</span>
                      <span className="text-xl font-bold text-green-600">{trader.profit}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-gray-50 rounded-xl text-center">
                        <div className="text-lg font-bold text-gray-900">{trader.winRate}</div>
                        <div className="text-xs text-gray-500">Win Rate</div>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-xl text-center">
                        <div className="text-lg font-bold text-gray-900">{trader.monthlyReturn}</div>
                        <div className="text-xs text-gray-500">Monthly</div>
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    <div className="flex items-center space-x-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        trader.risk === 'Low' ? 'bg-green-100 text-green-700' :
                        trader.risk === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {trader.risk}
                      </span>
                      <span className="text-xs text-gray-500">{trader.copiers} copiers</span>
                    </div>
                    <button className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-lg transition-colors duration-200">
                      Copy
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Features Grid */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {[
              {
                icon: '📊',
                title: 'Advanced Analytics',
                description: 'Real-time performance metrics and comprehensive risk analysis for every trader'
              },
              {
                icon: '🔒',
                title: 'Secure & Transparent',
                description: 'All trades verified and executed through bank-level security protocols'
              },
              {
                icon: '💰',
                title: 'Flexible Investment',
                description: 'Start with as little as $100 and scale your portfolio as you grow'
              }
            ].map((feature, index) => (
              <div key={index} className="text-center p-8 bg-white rounded-2xl border-2 border-gray-200 hover:border-amber-500 transition-all duration-300 hover:shadow-lg">
                <div className="text-5xl mb-4">{feature.icon}</div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h4>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center mt-12"
          >
            <Link
              to="/copy-trading"
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white font-bold text-lg rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Start Copy Trading
              <ArrowRightIcon className="ml-2 h-5 w-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Live Market Data Section - Redesigned */}
      <section className="relative py-24 lg:py-32 bg-black overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 right-0 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl"></div>
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 z-10">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-3 mb-6">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-amber-500"></div>
              <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-amber-500"></div>
            </div>
            
            <h2 className="text-5xl lg:text-6xl font-bold text-white mb-6">
              Real-Time Market Intelligence
            </h2>
            
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Professional-grade market data with institutional accuracy. Track crypto, stocks, and indices in real-time.
            </p>
          </motion.div>

          {/* Market Tabs */}
          <div className="flex justify-center mb-12">
            <div className="bg-slate-800/50 rounded-2xl p-2 backdrop-blur-sm border border-slate-700/50">
              {['crypto', 'stocks', 'indices'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveMarketTab(tab)}
                  className={`px-8 py-4 rounded-xl font-semibold transition-all duration-300 capitalize ${
                    activeMarketTab === tab
                      ? 'bg-amber-500 text-white shadow-lg'
                      : 'text-slate-300 hover:text-white hover:bg-slate-700/50'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          {/* Market Data Display */}
          <motion.div
            key={activeMarketTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-3xl border-2 border-amber-500/20 p-8 shadow-2xl"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-2xl font-bold text-white mb-2">
                  {activeMarketTab === 'crypto' ? 'Top Cryptocurrencies' : 
                   activeMarketTab === 'stocks' ? 'Major US Stocks' : 'Market Indices'}
                </h3>
                <p className="text-gray-400">Updated in real-time</p>
              </div>
              <div className="flex items-center space-x-2 bg-green-500/20 px-4 py-2 rounded-full border border-green-500/30">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-green-400 font-semibold text-sm">LIVE</span>
              </div>
            </div>

            {/* Market Data List */}
            <div className="space-y-3">
              {(activeMarketTab === 'crypto' ? liveMarketData.crypto : 
                activeMarketTab === 'stocks' ? liveMarketData.stocks : 
                liveMarketData.indices).slice(0, 6).map((item, index) => (
                <motion.div
                  key={item.symbol || index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl hover:bg-slate-800 transition-colors duration-200 border border-slate-700/50"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-500/20 to-yellow-500/20 rounded-xl flex items-center justify-center border border-amber-500/30">
                      <span className="text-amber-400 font-bold text-sm">
                        {item.symbol?.charAt(0) || '?'}
                      </span>
                    </div>
                    <div>
                      <h4 className="font-bold text-white text-lg">{item.symbol}</h4>
                      <p className="text-gray-400 text-sm">{item.name || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-white text-lg">
                      ${typeof item.price === 'number' ? item.price.toFixed(2) : item.price}
                    </div>
                    <div className={`text-sm font-medium ${
                      item.positive ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {item.positive ? '↗' : '↘'} {Math.abs(parseFloat(item.change) || 0).toFixed(2)}%
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Advanced Analytics Section - Redesigned */}
      <section className="relative py-24 lg:py-32 bg-white overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div className="space-y-6">
                <div className="inline-flex items-center px-4 py-2 bg-amber-50 border border-amber-200 rounded-full">
                  <span className="text-amber-700 text-sm font-medium">Advanced Analytics</span>
                </div>
                
                <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                  Professional Market
                  <span className="block bg-gradient-to-r from-amber-500 to-yellow-500 bg-clip-text text-transparent mt-2">
                    Research Tools
                  </span>
                </h2>
                
                <p className="text-xl text-gray-600 leading-relaxed">
                  Access institutional-grade research and analytics powered by real-time data 
                  and advanced charting tools used by professional traders worldwide.
                </p>
              </div>

              <div className="space-y-6">
                {[
                  { icon: ChartBarIcon, title: 'Real-Time Data', desc: 'Live market data with advanced technical indicators and pattern recognition.' },
                  { icon: BoltIcon, title: 'AI Insights', desc: 'Machine learning algorithms provide actionable trading signals and forecasts.' },
                  { icon: ShieldCheckIcon, title: 'Risk Management', desc: 'Comprehensive portfolio analysis with intelligent risk assessment tools.' }
                ].map((feature, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                      <feature.icon className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h4>
                      <p className="text-gray-600">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="pt-4">
                <Link
                  to="/news"
                  className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white font-semibold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  Explore Research Tools
                  <ArrowRightIcon className="ml-2 h-5 w-5" />
                </Link>
              </div>
            </motion.div>

            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden border-2 border-gray-200 shadow-2xl">
                <img
                  src="/images/21489.jpg"
                  alt="Professional analysts analyzing financial data"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute top-6 right-6 bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg border border-gray-200">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900">94.7%</div>
                    <div className="text-xs text-gray-600">Accuracy Rate</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Expert Team Section - Redesigned */}
      <section className="relative py-24 lg:py-32 bg-black overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 right-0 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl"></div>
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden border-2 border-amber-500/30 shadow-2xl">
                <img
                  src="/images/21491.jpg"
                  alt="Professional trading team"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute top-6 left-6 bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-white font-semibold text-sm">Live Trading Floor</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="space-y-8"
            >
              <div className="space-y-6">
                <div className="inline-flex items-center px-4 py-2 bg-amber-500/10 border border-amber-400/20 rounded-full">
                  <span className="text-amber-300 text-sm font-medium">Expert Team</span>
                </div>
                
                <h2 className="text-5xl lg:text-6xl font-bold text-white leading-tight">
                  World-Class Trading
                  <span className="block bg-gradient-to-r from-amber-400 to-yellow-400 bg-clip-text text-transparent mt-2">
                    Professionals
                  </span>
                </h2>
                
                <p className="text-xl text-gray-300 leading-relaxed">
                  Our team of seasoned professionals brings decades of Wall Street experience, 
                  providing you with institutional-grade support and market insights.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-6">
                {[
                  { value: '25+', label: 'Years Experience', color: 'amber' },
                  { value: '$2.5B+', label: 'Assets Managed', color: 'yellow' },
                  { value: '24/7', label: 'Market Monitoring', color: 'amber' },
                  { value: '50+', label: 'Countries Served', color: 'yellow' }
                ].map((stat, index) => (
                  <div key={index} className="text-center p-6 bg-slate-800/50 border border-slate-700/50 rounded-xl hover:border-amber-500/50 transition-all duration-300">
                    <div className={`text-3xl font-bold mb-2 text-${stat.color}-400`}>{stat.value}</div>
                    <div className="text-sm text-gray-400">{stat.label}</div>
                  </div>
                ))}
              </div>

              <div className="pt-4">
                <Link
                  to="/about"
                  className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white font-semibold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  Meet Our Team
                  <ArrowRightIcon className="ml-2 h-5 w-5" />
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Technology Innovation Section - Redesigned */}
      <section className="relative py-24 lg:py-32 bg-white overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div className="space-y-6">
                <div className="inline-flex items-center px-4 py-2 bg-amber-50 border border-amber-200 rounded-full">
                  <span className="text-amber-700 text-sm font-medium">Next-Gen Technology</span>
                </div>
                
                <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                  Cutting-Edge Trading
                  <span className="block bg-gradient-to-r from-amber-500 to-yellow-500 bg-clip-text text-transparent mt-2">
                    Infrastructure
                  </span>
                </h2>
                
                <p className="text-xl text-gray-600 leading-relaxed">
                  Experience lightning-fast execution and enterprise-grade security with our 
                  state-of-the-art trading infrastructure built for the modern trader.
                </p>
              </div>

              <div className="space-y-6">
                {[
                  { icon: BoltIcon, title: 'Lightning Speed', desc: 'Sub-millisecond execution with advanced order routing technology.' },
                  { icon: LockClosedIcon, title: 'Bank-Level Security', desc: 'Military-grade encryption and multi-layer security protocols.' },
                  { icon: GlobeAltIcon, title: 'Global Infrastructure', desc: 'Worldwide server network ensuring 99.99% uptime guarantee.' }
                ].map((feature, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                      <feature.icon className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h4>
                      <p className="text-gray-600">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="pt-4">
                <Link
                  to="/trading"
                  className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white font-semibold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  Experience Platform
                  <ArrowRightIcon className="ml-2 h-5 w-5" />
                </Link>
              </div>
            </motion.div>

            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden border-2 border-gray-200 shadow-2xl">
                <img
                  src="/images/21533.jpg"
                  alt="Futuristic trading technology"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute top-6 right-6 bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg border border-gray-200">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900">&lt;1ms</div>
                    <div className="text-xs text-gray-600">Latency</div>
                  </div>
                </div>
                <div className="absolute bottom-6 left-6 bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg border border-gray-200">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">99.99%</div>
                    <div className="text-xs text-gray-600">Uptime</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section - Before Footer */}
      <section className="relative py-24 lg:py-32 bg-gradient-to-br from-slate-950 via-slate-900 to-black overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 right-0 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-yellow-500/10 rounded-full blur-3xl"></div>
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Content Side */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div className="space-y-6">
                <div className="inline-flex items-center px-4 py-2 bg-amber-500/10 border border-amber-400/20 rounded-full">
                  <span className="text-amber-300 text-sm font-medium">Start Trading Today</span>
                </div>
                
                <h2 className="text-5xl lg:text-6xl font-bold text-white leading-tight">
                  Ready to Start Your
                  <span className="block bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 bg-clip-text text-transparent mt-2">
                    Trading Journey?
                  </span>
                </h2>
                
                <p className="text-xl text-gray-300 leading-relaxed">
                  Join thousands of successful traders who trust IMEXS BROKERAGE for their trading needs. 
                  Get started in minutes with our easy-to-use platform.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white font-bold text-lg rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-amber-500/50"
                >
                  Get Started Free
                  <ArrowRightIcon className="ml-2 h-5 w-5" />
                </Link>
                <Link
                  to="/trading"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 hover:border-amber-500/50 text-white font-semibold text-lg rounded-xl transition-all duration-300"
                >
                  Explore Platform
                </Link>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 pt-8 border-t border-gray-800">
                <div className="text-center">
                  <div className="text-3xl font-bold text-amber-400 mb-1">500K+</div>
                  <div className="text-sm text-gray-400">Active Traders</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-amber-400 mb-1">$50B+</div>
                  <div className="text-sm text-gray-400">Trading Volume</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-amber-400 mb-1">150+</div>
                  <div className="text-sm text-gray-400">Countries</div>
                </div>
              </div>
            </motion.div>

            {/* Image Side */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden border-2 border-amber-500/30 shadow-2xl group">
                {/* Glow effect */}
                <div className="absolute -inset-1 bg-gradient-to-r from-amber-500/20 via-yellow-500/20 to-amber-500/20 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <img
                  src="/images/24379.jpg"
                  alt="Professional trading platform"
                  className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-700"
                  onError={(e) => {
                    e.target.src = '/images/21533.jpg';
                  }}
                />
                
                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                
                {/* Floating stats cards */}
                <div className="absolute top-6 right-6 bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 shadow-lg">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-white">&lt;1ms</div>
                    <div className="text-xs text-gray-300">Latency</div>
                  </div>
                </div>
                <div className="absolute bottom-6 left-6 bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 shadow-lg">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-400">99.99%</div>
                    <div className="text-xs text-gray-300">Uptime</div>
                  </div>
                </div>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute -bottom-8 -right-8 w-40 h-40 bg-amber-500/10 rounded-full blur-3xl"></div>
              <div className="absolute top-1/2 -left-6 w-24 h-24 bg-yellow-500/10 rounded-full blur-2xl"></div>
            </motion.div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Home; 